export function toDate(value) {
  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getTimeMs(value) {
  const date = toDate(value);
  return date ? date.getTime() : 0;
}

export function calculateHours(start, end) {
  const startMs = getTimeMs(start);
  let endMs = getTimeMs(end);

  if (!startMs || !endMs) return 0;

  // Supports overnight shifts like 10 PM - 6 AM when same-date time values are used.
  if (endMs <= startMs) {
    endMs += 24 * 60 * 60 * 1000;
  }

  return Math.round(((endMs - startMs) / 36e5) * 100) / 100;
}

export function rangesOverlap(startA, endA, startB, endB) {
  const aStart = getTimeMs(startA);
  let aEnd = getTimeMs(endA);
  const bStart = getTimeMs(startB);
  let bEnd = getTimeMs(endB);

  if (!aStart || !aEnd || !bStart || !bEnd) return false;

  if (aEnd <= aStart) aEnd += 24 * 60 * 60 * 1000;
  if (bEnd <= bStart) bEnd += 24 * 60 * 60 * 1000;

  return aStart < bEnd && bStart < aEnd;
}

export function isPast(value) {
  const ms = getTimeMs(value);
  return ms > 0 && ms < Date.now();
}

export function isFuture(value) {
  const ms = getTimeMs(value);
  return ms > Date.now();
}

export function isToday(value) {
  const date = toDate(value);
  if (!date) return false;

  const now = new Date();

  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function isWithinHoursBefore(start, hours = 2) {
  const startMs = getTimeMs(start);
  if (!startMs) return false;

  const now = Date.now();
  const windowMs = Number(hours || 0) * 60 * 60 * 1000;

  return now >= startMs - windowMs && now <= startMs;
}

export function hasEnded(end) {
  const endMs = getTimeMs(end);
  return endMs > 0 && endMs < Date.now();
}

export function formatShortDate(value) {
  const date = toDate(value);
  if (!date) return "";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

export function formatShortTime(value) {
  const date = toDate(value);
  if (!date) return "";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatShiftTimeRange(start, end) {
  const startText = formatShortTime(start);
  const endText = formatShortTime(end);

  if (!startText && !endText) return "";
  return `${startText} - ${endText}`;
}

// Display-only conversion. Serialized Firestore timestamps do not have toDate().
export function formatDateTime(value, fallback = "") {
  try {
    const isSerializedTimestamp = value && typeof value === "object" &&
      typeof value.toDate !== "function" && Number.isFinite(value.seconds);
    const nanoseconds = isSerializedTimestamp ? (value.nanoseconds ?? 0) : 0;
    if (!Number.isFinite(nanoseconds)) return fallback;
    const date = toDate(isSerializedTimestamp
      ? new Date(value.seconds * 1000 + nanoseconds / 1e6)
      : value);
    return date ? date.toLocaleString() : fallback;
  } catch {
    return fallback;
  }
}
